import styled from 'styled-components';
import { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

type MTCNResponse = {
  refundId: number;
  key: string;
  transferAmount: number;
  currency: string;
  senderName: string;
};

type Props = {
  data: MTCNResponse[];
};

export const MTCN = ({ data = [] }: Props) => {
  const [activeIndex, setActiveIndex] = useState(0);
  return (
    <Wrapper>
      <Title>MTCN Information</Title>
      <Explain>Please present the information below at Western Union.</Explain>
      <CustomSwiper
        spaceBetween={50}
        slidesPerView={1}
        onSlideChange={(swiper) =>
          setActiveIndex(swiper.realIndex ?? swiper.activeIndex)
        }
      >
        {data.map((mtcn) => {
          return (
            <SwiperSlide>
              <Card>
                <CardTopControls>
                  {data.map((_, idx) =>
                    idx === activeIndex ? (
                      <ControlDash key={idx} />
                    ) : (
                      <ControlDot key={idx} />
                    ),
                  )}
                </CardTopControls>
                <Section>
                  <DataTitle>MTCN Code</DataTitle>
                  <DataValueLarge>{mtcn.key}</DataValueLarge>
                </Section>
                <TwoColumn>
                  <Field>
                    <DataTitle>Amount</DataTitle>
                    <DataValue>{`${mtcn.transferAmount.toLocaleString()} ${mtcn.currency}`}</DataValue>
                  </Field>
                  <Field>
                    <DataTitle>Sender Name</DataTitle>
                    <DataValue>{mtcn.senderName}</DataValue>
                  </Field>
                </TwoColumn>
              </Card>
            </SwiperSlide>
          );
        })}
      </CustomSwiper>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  border-radius: 20px;
  background: #246cf6;
  box-shadow: 0 0 20px 0 rgba(174, 191, 204, 0.5);
  padding: 20px;
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
`;

const Title = styled.strong`
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  line-height: 150%;
  letter-spacing: -0.36px;
  width: 100%;
`;

const Explain = styled.p`
  color: #f4f8ff;
  font-size: 14px;
  font-weight: 500;
  line-height: 140%;
  letter-spacing: -0.28px;
  width: 100%;
`;

const DataTitle = styled.dt`
  color: var(--KTP-Grayscale-80, var(--KTP-Color-Mono-80, #3a3b3e));
  font-size: 14px;
  font-weight: 600;
  line-height: 140%; /* 19.6px */
  letter-spacing: -0.28px;
`;

const DataValue = styled.dd`
  border-radius: 4px;
  background: var(--KTP-Color-Mono-05, #f5f6f7);
  color: var(--KTP-Grayscale-Gray_05, #777);
  font-size: 14px;
  font-weight: 500;
  line-height: 150%; /* 21px */
  letter-spacing: -0.28px;
  padding: 8px 12px;
  margin: 4px 0 0;
`;

const DataValueLarge = styled.dd`
  border-radius: 4px;
  background: var(--KTP-Color-Mono-05, #f5f6f7);
  color: var(--KTP-Grayscale-80, var(--KTP-Color-Mono-80, #3a3b3e));
  font-size: 18px;
  font-weight: 700;
  line-height: 150%;
  letter-spacing: -0.36px;
  padding: 8px 12px;
  margin: 4px 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 42px;
`;

const Card = styled.div`
  width: 100%;
  background: #fff;
  border-radius: 12px;
  padding: 24px 12px 12px 12px;
  position: relative;
`;

const Section = styled.div`
  width: 100%;
`;

const TwoColumn = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

const Field = styled.div`
  flex: 1 1 0;
`;

const CustomSwiper = styled(Swiper)`
  width: 100%;
`;

const CardTopControls = styled.div`
  position: absolute;
  top: 10px;
  right: 10px; /* 배지와 겹치지 않도록 간격 확보 */
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ControlDash = styled.span`
  display: block;
  width: 18px;
  height: 4px;
  background: #246cf6;
  border-radius: 2px;
`;

const ControlDot = styled.span`
  display: block;
  width: 6px;
  height: 6px;
  background: #dbdbdb;
  border-radius: 50%;
`;

/* badge removed per latest design */
